import { FormikProvider, setNestedObjectValues, useFormik } from 'formik';
import { Action, Location } from 'history';
import Moment from 'moment';
import Delta from 'quill-delta';
import { PropsWithChildren, ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Prompt, Route, useHistory } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useErrorBoundary, useLocalize, useViewerRoles } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	ArchiveIcon,
	Button,
	Card,
	CloseIcon,
	CopyIcon,
	DatePicker,
	Empty,
	FlexGap,
	GraphQLErrorMessage,
	Heading,
	Heading3,
	IconButton,
	JunkIcon,
	Paragraph,
	PencilEditBoxIcon,
	Pill,
	Popover,
	Select,
	SettingsSection,
	Spinner,
	WarningIcon,
	tokens,
	useToasts,
} from '@treams/treams-ui';

import DetailsTableForm from '../../packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableForm';
import { DetailsTableFormValueTypes } from '../../packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableFormValue';
import TooltipDark from '../../packages/kakapo-ui-kit/components/tooltips/TooltipDark';
import validator from '../../packages/kakapo-ui-services/factories/ValidatorFactory';
import AddMomentButton from './components/AddMomentButton';
import MomentTemplateModal from './moment-templates/MomentTemplateModal';

const FIVE_YEARS_IN_DAYS = 365 * 5;

export const DURATION_SCALES = [
	{
		scale: 'hour',
		nextThreshold: 12,
		nextFactor: 24,
	},
	{
		scale: 'day',
		nextThreshold: 14,
		nextFactor: 7,
	},
	{
		scale: 'week',
		nextThreshold: 8,
		nextFactor: 4,
	},
	{
		scale: 'month',
		nextThreshold: 12,
		nextFactor: 12,
	},
	{
		scale: 'year',
	},
];

const TableCell = styled(Box)`
	color: ${tokens.text.secondary};
`;

const TableHeader = styled(TableCell)`
	font-weight: bold;
	font-size: 12px;
`;

const InlineValidationError = styled(Flex)`
	position: relative;
	align-items: center;
	color: ${tokens.error.text};
`;

const MomentRow = styled(Flex)`
	padding: ${tokens.gap.group}px ${tokens.padding.container}px;
	border-bottom: 1px solid ${tokens.border.subtle};

	&:last-child {
		border-bottom: 0;
	}
`;

const formatDuration = (t, tKey, originalTimeInDays) => {
	let time = 0 + originalTimeInDays;
	let timeText;

	if (time === 0) {
		timeText = t(`${tKey}.today`);
		return { time, timeText };
	}

	timeText = t(`${tKey}.hour`, { count: time });

	for (let i = 0; i < DURATION_SCALES.length - 1; i += 1) {
		if (time < DURATION_SCALES[i]!.nextThreshold!) {
			break;
		}

		time = Math.round(time / DURATION_SCALES[i]!.nextFactor!);
		timeText = t(`${tKey}.${DURATION_SCALES[i + 1]!.scale}`, {
			count: time,
		});
	}

	return { time, timeText };
};

function InlineValidation(props: PropsWithChildren<{ valid: boolean; message: string }>) {
	const [hover, setHover] = useState(false);

	if (props.valid) {
		return <>{props.children}</>;
	}

	return (
		<InlineValidationError onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
			<TooltipDark direction="north" isHovered={hover}>
				{props.message}
			</TooltipDark>
			<Box flexGrow={0} flexShrink={0} mr={2}>
				<Box size={16}>
					<WarningIcon />
				</Box>
			</Box>
			{props.children}
		</InlineValidationError>
	);
}

function mapMomentNotificationToInput(
	momentNotification: graphql.MomentNotification,
	stripID?: boolean,
): graphql.MomentNotificationInput {
	return {
		id: stripID
			? undefined
			: momentNotification.id?.startsWith('_')
				? undefined
				: momentNotification.id,
		offset: momentNotification.offset,
		type: momentNotification.type,
	};
}

function mapMomentFeedbackToInput(
	momentFeedback: graphql.MomentFeedback,
): graphql.MomentFeedbackInput {
	const input: graphql.MomentFeedbackInput = {};

	if (momentFeedback.feedbackTemplate) {
		input.feedbackTemplateID = momentFeedback.feedbackTemplate.id;
	}

	if (momentFeedback.minimumQuestions) {
		input.minimumQuestions = momentFeedback.minimumQuestions;
	}

	if (momentFeedback.minimumRecipients) {
		input.minimumRecipients = momentFeedback.minimumRecipients;
	}

	if (momentFeedback.maximumRecipients) {
		input.maximumRecipients = momentFeedback.maximumRecipients;
	}

	input.releaseDate = momentFeedback.releaseDate ?? null;

	return input;
}

function mapMomentVariationsToInput(
	type: graphql.MomentType,
	momentVariations: graphql.MomentVariation[],
): graphql.MomentVariationInput[] {
	return momentVariations
		.filter((momentVariation) => {
			// Drop variations whose payload doesn't match the moment type (e.g.
			// leftovers from cloning or legacy data) so we never persist
			// meaningless variations with an empty template reference.
			if (type === graphql.MomentType.Evaluation) {
				return !!momentVariation.evaluation?.evaluationForm?.id;
			}

			if (type === graphql.MomentType.Feedback) {
				return !!momentVariation.feedback?.feedbackTemplate?.id;
			}

			return true;
		})
		.map((momentVariation) => ({
			evaluation:
				type === graphql.MomentType.Evaluation
					? {
							evaluationFormID: momentVariation.evaluation?.evaluationForm?.id,
						}
					: undefined,
			feedback:
				type === graphql.MomentType.Feedback
					? {
							feedbackTemplateID: momentVariation.feedback?.feedbackTemplate?.id,
						}
					: undefined,
			goals: momentVariation.goals
				? {
						minimumGoals: momentVariation.goals.minimumGoals,
						minimumCompetencyGoals: momentVariation.goals.minimumCompetencyGoals,
					}
				: undefined,
			jobTitleID: momentVariation.jobTitle?.id,
			roleType: momentVariation.roleType ?? undefined,
		}));
}

function mapMomentToInput(
	moment: graphql.MomentTemplate,
	stripID?: boolean,
): graphql.MomentTemplateInput {
	return {
		id: stripID ? undefined : moment.id.startsWith('_') ? undefined : moment.id,
		duration: moment.duration,
		startOffset: moment.startOffset - 1,
		title: moment.title,
		type: moment.type,
		description: moment.description,
		instruction: moment.instruction,
		evaluation: moment.evaluation
			? {
					evaluationFormID: moment.evaluation.evaluationForm?.id,
				}
			: undefined,
		feedback: moment.feedback ? mapMomentFeedbackToInput(moment.feedback) : undefined,
		goals: moment.goals
			? {
					minimumGoals: moment.goals.minimumGoals,
					minimumCompetencyGoals: moment.goals.minimumCompetencyGoals,
				}
			: undefined,
		variations: moment.variations
			? mapMomentVariationsToInput(moment.type, moment.variations)
			: undefined,
		notifications: moment.notifications.map((notification) =>
			mapMomentNotificationToInput(notification, stripID),
		),
		showToUser: moment.showToUser,
		talentReview: moment.talentReview?.template?.id
			? { templateID: moment.talentReview.template.id }
			: undefined,
	};
}

function ManagerRoleCell({ type }: { type: graphql.MomentType }) {
	const { t } = useTranslation();
	const [hover, setHover] = useState(false);
	const isManagers = type === graphql.MomentType.TalentReview;
	const key = isManagers ? 'managers' : 'everyone';

	return (
		<Box
			style={{ position: 'relative' }}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
		>
			<TooltipDark direction="north" isHovered={hover}>
				{t(`common.settings.cycleTemplates.audience.${key}Tooltip`)}
			</TooltipDark>
			<Paragraph>{t(`common.settings.cycleTemplates.audience.${key}`)}</Paragraph>
		</Box>
	);
}

function CycleTemplateEditor(props: { cycleTemplate?: graphql.CycleTemplate }) {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const { isHRBusinessPartner } = useViewerRoles();
	const [previewDate, setPreviewDate] = useState<Date | undefined>();
	const [previewPickerOpen, setPreviewPickerOpen] = useState(false);

	const formik = useFormik({
		initialValues: {
			title: props.cycleTemplate?.title ?? [],
			description: props.cycleTemplate?.description ?? [],
			successorID: props.cycleTemplate?.successor?.id ?? undefined,
			repeats: !!props.cycleTemplate?.successor,
			duration: props.cycleTemplate?.duration.toFixed() ?? '12',
			automatic: props.cycleTemplate?.automatic ?? false,
			groupIDs: props.cycleTemplate?.groups?.map(({ id }) => id) ?? [],
			excludedGroupIDs: props.cycleTemplate?.excludedGroups?.map(({ id }) => id) ?? [],
			jobTitleIDs: props.cycleTemplate?.jobTitles?.map(({ id }) => id) ?? [],
			excludedJobTitleIDs: props.cycleTemplate?.excludedJobTitles?.map(({ id }) => id) ?? [],
			interval: props.cycleTemplate?.interval ?? undefined,
			employmentType: props.cycleTemplate?.employmentType ?? undefined,
			moments:
				props.cycleTemplate?.moments.map((moment) => ({
					...moment,
					startOffset: moment.startOffset + 1,
					goals: moment.goals && {
						minimumGoals: moment.goals.minimumGoals,
						minimumCompetencyGoals: moment.goals.minimumCompetencyGoals ?? 0,
					},
				})) ?? [],
		},
		onSubmit: () => handleSubmit(),
		validationSchema: validator.object({
			title: validator.localizedString(),
			duration: validator.number().min(1),
			moments: validator.array(
				validator.object({
					startOffset: validator.number().min(1),
					duration: validator.number().min(1),
					notifications: validator.array(
						validator.object({
							type: validator.string(),
							offset: validator.number().min(0).max(366),
						}),
					),
				}),
			),
		}),
		enableReinitialize: true,
	});

	const groups = graphql.useGroupsQuery({
		variables: {
			first: 1000,
			filter: {
				isArchived: false,
				types: [graphql.GroupType.Department],
			},
		},
	});

	const jobTitles = graphql.useJobTitlesQuery({
		variables: {
			first: 1000,
			isArchived: false,
		},
	});

	const templates = graphql.useCycleTemplatesQuery({
		variables: {
			first: 1000,
			isArchived: false,
		},
		skip: !formik.values.repeats,
	});

	useEffect(() => {
		if (formik.values.repeats === !!formik.values.successorID) {
			return;
		}

		if (formik.values.repeats) {
			formik.setFieldValue('successorID', props.cycleTemplate?.id ?? '$repeat');
		} else {
			formik.setFieldValue('successorID', undefined);
		}
	}, [formik.values.repeats]);

	useEffect(() => {
		(async () => {
			await formik.validateForm();
			formik.setTouched(setNestedObjectValues(formik.values, true));
		})();
	}, []);

	const [error, attempt, reset] = useErrorBoundary();

	const [create, { loading: createLoading }] = graphql.useCreateCycleTemplateMutation();
	const [update, { loading: updateLoading }] = graphql.useUpdateCycleTemplateMutation();
	const [archive, { loading: archiveLoading }] = graphql.useArchiveCycleTemplateMutation();
	const [restore, { loading: restoreLoading }] = graphql.useRestoreCycleTemplateMutation();
	const loading = createLoading || updateLoading;

	const handleSubmit = attempt(async () => {
		reset();

		const input = {
			title: formik.values.title.map((title) => ({
				languageID: title.languageID,
				text: title.text,
			})),
			description: formik.values.description.map((description) => ({
				languageID: description.languageID,
				text: description.text,
			})),
			automatic: formik.values.automatic,
			groupIDs: formik.values.groupIDs.length ? formik.values.groupIDs : undefined,
			excludedGroupIDs: formik.values.excludedGroupIDs.length
				? formik.values.excludedGroupIDs
				: undefined,
			jobTitleIDs: formik.values.jobTitleIDs.length ? formik.values.jobTitleIDs : undefined,
			excludedJobTitleIDs: formik.values.excludedJobTitleIDs.length
				? formik.values.excludedJobTitleIDs
				: undefined,
			interval: formik.values.interval,
			employmentType: formik.values.employmentType,
		};

		if (props.cycleTemplate) {
			await update({
				variables: {
					input: {
						id: props.cycleTemplate.id,
						duration: parseInt(formik.values.duration),
						successorID: formik.values.successorID,
						moments: formik.values.moments.map((moment) => mapMomentToInput(moment)),
						...input,
					},
				},
			});
		} else {
			const { data } = await create({
				variables: {
					input: {
						moments: formik.values.moments.map((moment) => mapMomentToInput(moment)),
						duration: parseInt(formik.values.duration),
						successorID: formik.values.successorID,
						...input,
					},
				},
			});

			if (data) {
				history.push(`/settings/cycles/${data.createCycleTemplate.cycleTemplate.id}`);
			}
		}

		addToast(t('common.settings.cycleTemplates.edit.success'), {
			appearance: 'success',
			autoDismiss: true,
		});
	});

	const handleDuplicate = attempt(async () => {
		const input = {
			title: formik.values.title.map((title) => ({
				languageID: title.languageID,
				text: t('common.copyOf', { text: title.text, lng: title.languageID.split('_')[0] }),
			})),
			description: formik.values.description.map((description) => ({
				languageID: description.languageID,
				text: description.text,
			})),
			automatic: formik.values.automatic,
			groupIDs: formik.values.groupIDs.length ? formik.values.groupIDs : undefined,
			excludedGroupIDs: formik.values.excludedGroupIDs ? formik.values.excludedGroupIDs : undefined,
			jobTitleIDs: formik.values.jobTitleIDs.length ? formik.values.jobTitleIDs : undefined,
			excludedJobTitleIDs: formik.values.excludedJobTitleIDs
				? formik.values.excludedJobTitleIDs
				: undefined,
			interval: formik.values.interval,
			employmentType: formik.values.employmentType,
		};

		const { data } = await create({
			variables: {
				input: {
					moments: formik.values.moments.map((moment) => mapMomentToInput(moment, true)),
					duration: parseInt(formik.values.duration),
					successorID: formik.values.successorID,
					...input,
				},
			},
		});

		if (data) {
			history.push(`/settings/cycles/${data.createCycleTemplate.cycleTemplate.id}`);
		}
	});

	const history = useHistory();

	const { localize, language } = useLocalize();

	const { pathname } = history.location;

	/**
	 * Adds a new moment
	 * @param type - What type of moment it will be
	 * @param moment - The moment to clone from
	 */
	const addMomentHandler = (type?: graphql.MomentType, moment?: graphql.MomentTemplate) => {
		const newId = '_' + Math.random().toString();
		const notifications: graphql.MomentNotification[] = [];
		let values: graphql.MomentTemplate = {} as graphql.MomentTemplate;

		// If moment data is given, clone from that
		if (moment) {
			// Filter out id from the data set
			const { title, ...rest } = moment;

			const duplicateMomentTitle = concatLocalizedStrings(
				title,
				t('common.settings.cycleTemplates.copy.duplicateMoment'),
			);

			values = {
				...rest,
				id: newId,
				title: duplicateMomentTitle,
			};
		} else if (type) {
			// Add a notification by default (for both normal and message moments)
			// For normal moments the user can remove this notification.
			notifications.push({
				type: graphql.MomentNotificationType.BeforeStart,
				offset: 0,
			} as graphql.MomentNotification);

			values = {
				id: newId,
				duration: 1,
				startOffset: 1,
				notifications,
				isArchived: false,
				title: [
					{
						languageID: language.id,
						text: t('common.settings.cycleTemplates.edit.momentTitlePlaceholder'),
					},
				],
				description: [
					{
						languageID: language.id,
						text: new Delta([
							{
								insert: t(
									type === graphql.MomentType.TalentReview
										? 'common.settings.cycleTemplates.edit.talentReview.momentDescriptionPlaceholder'
										: 'common.settings.cycleTemplates.edit.momentDescriptionPlaceholder',
								),
							},
						]),
					},
				],
				instruction: [
					{
						languageID: language.id,
						text: new Delta([
							{
								insert: t('common.settings.cycleTemplates.edit.momentInstructionPlaceholder'),
							},
						]),
					},
				],
				showToUser: false,
				type,
			};

			history.push(`/settings/cycles/${props.cycleTemplate?.id ?? 'new'}/moments/${newId}`);
		}

		formik.setFieldValue(
			'moments',
			formik.values.moments.concat([
				{
					...values,
					goals: {
						minimumGoals: values.goals?.minimumGoals,
						minimumCompetencyGoals: values.goals?.minimumCompetencyGoals ?? 0,
					},
				},
			]),
		);
	};

	/**
	 * Creates a new moment from an existing moment
	 * @param moment - The moment to clone
	 */
	const cloneMomentHandler = (moment: graphql.MomentTemplate) => {
		addMomentHandler(undefined, moment);
	};

	const stripMomentFromPathname = (pathname: string) => {
		const momentsIndex = pathname.indexOf('/moments');

		if (momentsIndex !== -1) {
			return pathname.substring(0, momentsIndex);
		}

		return pathname;
	};

	const shouldShowPrompt = (location: Location, action: Action) => {
		const rootPath = stripMomentFromPathname(pathname);
		const isOnSamePage = location.pathname.startsWith(rootPath);

		if (isOnSamePage) {
			// Skip the prompt if we are on the same page.
			return true;
		}

		return t('common.settings.cycleTemplates.modal.warning');
	};

	return (
		<FormikProvider value={formik}>
			{/* We use the message prop to check if we should show the prompt,
			as the when prop does not have the next location as a parameter. */}
			<Prompt when={formik.dirty} message={shouldShowPrompt} />
			<FlexGap flexDirection="column" gap={tokens.gap.page} width="100%">
				<FlexGap alignItems="center" justifyContent="space-between" width="100%">
					<Heading mb={0}>
						{t(`common.settings.cycleTemplates.${props.cycleTemplate ? 'edit' : 'create'}.title`)}
						{props.cycleTemplate ? ` “${localize(props.cycleTemplate.title).text}”` : ''}
					</Heading>
					{props.cycleTemplate && (
						<FlexGap gap={tokens.gap.element}>
							<Button
								type="secondary"
								icon={<CopyIcon />}
								title={t('common.settings.cycleTemplates.duplicate.buttonTitle')}
								disabled={loading || createLoading || formik.dirty}
								onClick={handleDuplicate}
							/>
							{!isHRBusinessPartner && (
								<Button
									type="secondary"
									icon={<ArchiveIcon />}
									loading={archiveLoading || restoreLoading}
									title={props.cycleTemplate.isArchived ? t('common.restore') : t('common.archive')}
									onClick={attempt(async () => {
										const input = { variables: { input: { id: props.cycleTemplate!.id } } };
										if (props.cycleTemplate!.isArchived) {
											await restore(input);
										} else {
											await archive(input);
										}
										history.push('/settings/cycles');
									})}
								/>
							)}
						</FlexGap>
					)}
				</FlexGap>
				<FlexGap flexDirection="column" gap={tokens.gap.section} width="100%">
					<DetailsTableForm
						formValues={[
							{
								label: t('common.settings.cycleTemplates.edit.name'),
								name: 'title',
								type: DetailsTableFormValueTypes.Multilingual,
							},
							{
								label: t('common.settings.cycleTemplates.edit.description'),
								name: 'description',
								type: DetailsTableFormValueTypes.MultilingualQuill,
							},
							{
								label: t('common.settings.cycleTemplates.edit.duration'),
								name: 'duration',
								type: DetailsTableFormValueTypes.Text,
							},
							{
								label: t('common.settings.cycleTemplates.edit.repeats'),
								name: 'repeats',
								type: DetailsTableFormValueTypes.Switch,
							},
							...(formik.values.successorID
								? [
										{
											label: t('common.settings.cycleTemplates.edit.successor.label'),
											name: 'successorID',
											type: DetailsTableFormValueTypes.Select,
											options: [
												{
													label: t('common.settings.cycleTemplates.edit.successor.same'),
													value: props.cycleTemplate?.id ?? '$repeat',
												},
											].concat(
												templates.data?.cycleTemplates.edges
													.filter(({ node }) => node.id !== props.cycleTemplate?.id)
													.map(({ node }) => ({
														label: localize(node.title).text,
														value: node.id,
													})) ?? [],
											),
										},
									]
								: []),
						]}
					/>

					<SettingsSection
						title={t('common.settings.cycleTemplates.edit.automatic.label')}
						description={t('common.settings.cycleTemplates.edit.automatic.description')}
					>
						<DetailsTableForm
							formValues={[
								{
									label: t('common.settings.cycleTemplates.edit.automatic.enabled.label'),
									name: 'automatic',
									type: DetailsTableFormValueTypes.Switch,
								},
								...(formik.values.automatic
									? [
											{
												label: t('common.settings.cycleTemplates.edit.groups.label'),
												name: 'groupIDs',
												type: DetailsTableFormValueTypes.GroupSelector,
												options: groups.data?.groups.edges.map(({ node }) => ({
													value: node.id,
													label: node.name,
												})),
											},
											{
												label: t('common.settings.cycleTemplates.edit.excludedGroups.label'),
												name: 'excludedGroupIDs',
												type: DetailsTableFormValueTypes.GroupSelector,
												options: groups.data?.groups.edges.map(({ node }) => ({
													value: node.id,
													label: node.name,
												})),
											},
											{
												label: t('common.settings.cycleTemplates.edit.jobTitles.label'),
												name: 'jobTitleIDs',
												type: DetailsTableFormValueTypes.MultiSelect,
												options: jobTitles.data?.jobTitles.edges.map(({ node }) => ({
													value: node.id,
													label: localize(node.title).text,
												})),
											},
											{
												label: t('common.settings.cycleTemplates.edit.excludedJobTitles.label'),
												name: 'excludedJobTitleIDs',
												type: DetailsTableFormValueTypes.MultiSelect,
												options: jobTitles.data?.jobTitles.edges.map(({ node }) => ({
													value: node.id,
													label: localize(node.title).text,
												})),
											},
											{
												label: t('common.settings.cycleTemplates.edit.employmentType.label'),
												name: 'employmentType',
												type: DetailsTableFormValueTypes.Select,
												options: [
													{
														value: undefined as unknown as string,
														label: t('common.settings.cycleTemplates.edit.employmentType.everyone'),
													},
													{
														value: graphql.EmploymentType.Internal,
														label: t(
															'common.settings.cycleTemplates.edit.employmentType.onlyInternal',
														),
													},
													{
														value: graphql.EmploymentType.External,
														label: t(
															'common.settings.cycleTemplates.edit.employmentType.onlyExternal',
														),
													},
												],
											},
										]
									: []),
								{
									label: t('common.settings.cycleTemplates.edit.interval.label'),
									name: 'interval',
									type: DetailsTableFormValueTypes.Select,
									options: [
										{
											label: t('common.settings.cycleTemplates.edit.interval.options.immediately'),
											value: undefined as unknown as string,
										},
										{
											label: t('common.settings.cycleTemplates.edit.interval.options.yearly'),
											value: graphql.CycleTemplateInterval.Year,
										},
									],
								},
							]}
						/>
					</SettingsSection>

					<SettingsSection
						title={
							<Flex flex={1} alignItems="center">
								<Box mr={2}>
									<Heading3 mb={0}>{t('common.settings.cycleTemplates.edit.moments')}</Heading3>
								</Box>
								<AddMomentButton onSelect={(type) => addMomentHandler(type)} />
							</Flex>
						}
						actions={
							<Flex alignItems="center">
								<TableHeader width={100}>
									{previewDate
										? t('common.settings.cycleTemplates.previewStartDate.start')
										: t('common.settings.cycleTemplates.moments.when')}
								</TableHeader>
								<TableHeader width={100}>
									{previewDate
										? t('common.settings.cycleTemplates.previewStartDate.end')
										: t('common.settings.cycleTemplates.moments.duration')}
								</TableHeader>
								<TableHeader width={100}>
									{t('common.settings.cycleTemplates.audience.column')}
								</TableHeader>
								<TableHeader width={100}></TableHeader>
							</Flex>
						}
					>
						{formik.values.moments.length === 0 ? (
							<Empty.Card title={t('common.settings.cycleTemplates.moments.empty')} />
						) : (
							<Card p={0}>
								{formik.values.moments.map((moment, index) => (
									<MomentRow key={moment.id || index} data-list-item alignItems="center">
										<Flex flex={1} alignItems="center">
											<TableCell marginRight={12}>
												<Paragraph mb={0}>{localize(moment.title)?.text || ''}</Paragraph>
											</TableCell>
											<Pill tint="blue">
												{t(`common.settings.cycleTemplates.types.${moment.type.toLowerCase()}`)}
											</Pill>
										</Flex>
										<TableCell width={100}>
											<InlineValidation
												valid={
													moment.startOffset <
														Math.round((365 / 12) * parseInt(formik.values.duration)) &&
													moment.startOffset > 0
												}
												message={t('common.settings.cycleTemplates.whenOutOfRangeError')}
											>
												{previewDate
													? Moment(previewDate)
															.add(moment.startOffset - 1, 'days')
															.format('L')
													: t('common.settings.cycleTemplates.whenFormat', {
															days: moment.startOffset || '0',
														})}
											</InlineValidation>
										</TableCell>
										<TableCell width={100}>
											<InlineValidation
												valid={moment.duration > 0 && moment.duration <= FIVE_YEARS_IN_DAYS}
												message={t('common.settings.cycleTemplates.durationOutOfRangeError')}
											>
												{previewDate
													? Moment(previewDate)
															.add(moment.startOffset - 1 + moment.duration, 'days')
															.format('L')
													: formatDuration(
															t,
															'common.settings.cycleTemplates.duration',
															moment.duration * 24,
														).timeText}
											</InlineValidation>
										</TableCell>
										<TableCell width={100}>
											<ManagerRoleCell type={moment.type} />
										</TableCell>
										<TableCell width={110}>
											<Flex>
												<IconButton
													mr={2}
													type="secondary"
													icon={<CopyIcon />}
													onClick={() => cloneMomentHandler(moment)}
												/>

												<IconButton
													mr={2}
													type="secondary"
													icon={<PencilEditBoxIcon />}
													onClick={() => {
														history.push(
															`/settings/cycles/${props.cycleTemplate?.id ?? 'new'}/moments/${moment.id}`,
														);
													}}
												/>

												<IconButton
													icon={<JunkIcon />}
													type="destructive"
													onClick={() => {
														const moments = formik.values.moments.slice();
														moments.splice(index, 1);
														formik.setFieldValue('moments', moments);
													}}
												/>
											</Flex>
										</TableCell>
									</MomentRow>
								))}
							</Card>
						)}
					</SettingsSection>

					<Box>
						<GraphQLErrorMessage error={error} />

						<Flex justifyContent="space-between">
							<Button
								role="button"
								aria-label={t('common.save')}
								title={t('common.save')}
								loading={loading}
								onClick={() => formik.submitForm()}
							/>
							<FlexGap alignItems="center" gap={tokens.gap.element}>
								{previewDate && (
									<IconButton
										type="secondary"
										icon={<CloseIcon />}
										aria-label={t('common.close')}
										onClick={() => setPreviewDate(undefined)}
									/>
								)}
								<Popover.Target
									visible={previewPickerOpen}
									contents={
										<DatePicker
											value={previewDate}
											onChange={(date) => setPreviewDate(date)}
											onDismiss={() => setPreviewPickerOpen(false)}
										/>
									}
									onDismiss={() => setPreviewPickerOpen(false)}
								>
									<Select.Trigger
										fit
										placeholder={!previewDate}
										onClick={() => setPreviewPickerOpen(true)}
									>
										{`${t('common.settings.cycleTemplates.previewStartDate.picker')}: ${
											previewDate ? Moment(previewDate).format('LL') : t('datePicker.value.empty')
										}`}
									</Select.Trigger>
								</Popover.Target>
							</FlexGap>
						</Flex>
					</Box>
				</FlexGap>

				<Route path={`/settings/cycles/${props.cycleTemplate?.id ?? 'new'}/moments/:id`}>
					{({ match }) => {
						if (!match) {
							return null;
						}

						const index = formik.values.moments.findIndex(
							(moment) => moment.id === match.params.id,
						);

						if (index < 0) {
							return null;
						}

						return (
							<MomentTemplateModal
								name={`moments[${index}]`}
								url={`/settings/cycles/${props.cycleTemplate?.id ?? 'new'}/moments/${
									match.params.id
								}`}
								returnURL={`/settings/cycles/${props.cycleTemplate?.id ?? 'new'}`}
							/>
						);
					}}
				</Route>
			</FlexGap>
		</FormikProvider>
	);
}

function CycleTemplateEditorID(props: { id: string }): ReactElement {
	const { data, error, loading } = graphql.useCycleTemplateQuery({
		variables: { id: props.id },
	});

	if (loading) {
		return <Spinner centered />;
	} else if (error) {
		return <GraphQLErrorMessage error={error.graphQLErrors[0]} />;
	} else {
		return <CycleTemplateEditor cycleTemplate={data!.cycleTemplate as graphql.CycleTemplate} />;
	}
}

function concatLocalizedStrings(lhs: graphql.LocalizedString, rhs: string) {
	return lhs.map(({ languageID, text }) => {
		return {
			languageID,
			text: text + rhs,
		};
	});
}

export { CycleTemplateEditor as CycleTemplateCreator };

export default CycleTemplateEditorID;
